"use client";

import * as React from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../private/utils.js";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect } from "react";

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetContext = React.createContext<{
  isOpen: boolean;
  changeMenuState: (state: "open" | "close") => void;
}>({
  isOpen: false,
  changeMenuState: () => {}
});

const useSheet = () => {
  const context = React.useContext(SheetContext);
  if (context === undefined) {
    throw new Error("useSheet must be used within a SheetContext");
  }
  return context;
};

const SheetRoot = ({
  children,
  ...props
}: React.ComponentPropsWithoutRef<typeof SheetPrimitive.Root>) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const changeMenuState = (state: "open" | "close") => {
    setIsOpen(state === "open");
  };
  useEffect(() => {
    setIsOpen(props.open ?? false);
  }, [props.open]);

  // close on a tag click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (e.target instanceof HTMLElement) {
        // target includes class closeSheetOnClick or any parent includes it
        let element = e.target as HTMLElement | null;
        while (element) {
          if (element.classList.contains("closeSheetOnClick")) {
            changeMenuState("close");
          }
          element = element.parentElement;
        }
      }
    };
    if (isOpen) {
      document.addEventListener("click", handleClick);
    }
    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, [isOpen]);

  return (
    <SheetContext.Provider value={{ isOpen, changeMenuState }}>
      <SheetPrimitive.Root
        {...props}
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open);
          props?.onOpenChange?.(open);
        }}
      >
        {children}
      </SheetPrimitive.Root>
    </SheetContext.Provider>
  );
};
const SheetOverlay = ({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) => (
  <SheetPrimitive.Overlay
    className={cn(
      "bg-mono-100/80 dark:bg-mono-950/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 backdrop-blur-xs transition-all",
      className
    )}
    {...props}
  />
);

SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
  "fixed z-50 gap-4 bg-background px-4 pb-6 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-[90%] border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-full w-[90%]  border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm"
      }
    },
    defaultVariants: {
      side: "right"
    }
  }
);

interface SheetContentProps
  extends
    React.ComponentProps<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  sheetContainerId?: string;
}

const SheetContent = ({
  side = "right",
  className,
  children,
  sheetContainerId,
  ...props
}: SheetContentProps) => {
  const container = sheetContainerId
    ? document.getElementById(sheetContainerId)
    : undefined;

  return (
    <SheetPortal container={container} forceMount={props?.forceMount}>
      <SheetOverlay />
      <SheetPrimitive.Content
        className={cn(
          sheetVariants({ side }),
          "dark:bg-mono-950/50 dark:text-mono-200 dark:border-mono-700/50 backdrop-blur-lg",
          "bg-mono-100/20 text-mono-800 border-mono-200 overflow-scroll border-r backdrop-blur-lg ",
          container ? "absolute" : "fixed",
          className
        )}
        {...props}
      >
        {children}
        {/*<SheetPrimitive.Close className="absolute right-4 top-4 rounded-xs opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-mono-800 dark:data-[state=open]:bg-mono-200">*/}
        {/*  <XMarkIcon className="h-4 w-4" />*/}
        {/*  <span className="sr-only">Close</span>*/}
        {/*</SheetPrimitive.Close>*/}
      </SheetPrimitive.Content>
    </SheetPortal>
  );
};

SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetSubContent = ({
  side = "right",
  className,
  children,
  ...props
}: SheetContentProps) => {
  if (props.sheetContainerId) {
    return (
      <SheetContent side={side} className={className} {...props}>
        {children}
      </SheetContent>
    );
  }
  return (
    <>
      <SheetOverlay />
      <SheetPrimitive.Content
        className={cn(
          sheetVariants({ side }),
          "dark:text-mono-200 dark:border-mono-700/50 dark:bg-inherit",
          `bg-inherit/50 text-mono-800  border-mono-200 w-[calc(100%-20px)]`,
          className
        )}
        {...props}
      >
        {children}
        {/*<SheetPrimitive.Close className="absolute right-4 top-4 rounded-xs opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-mono-800 dark:data-[state=open]:bg-mono-200">*/}
        {/*  <XMarkIcon className="h-4 w-4" />*/}
        {/*  <span className="sr-only">Close</span>*/}
        {/*</SheetPrimitive.Close>*/}
      </SheetPrimitive.Content>
    </>
  );
};
SheetSubContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <div ref={ref} className={cn("sticky top-0  z-10    ", className)} {...props}>
    <>
      <div
        className={
          "bg-mono-100/80 dark:bg-mono-950/50 dark:border-mono-700/50 absolute inset-0  border-b backdrop-blur-sm backdrop-blur-lg"
        }
      />
      <div
        className={
          "relative flex  items-start justify-between gap-x-4 py-6 text-left sm:text-left"
        }
      >
        {children}
        <SheetPrimitive.Close className=" ring-offset-background focus-visible:ring-ring data-[state=open]:bg-mono-800 dark:data-[state=open]:bg-mono-200 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none">
          <XMarkIcon className="size-6" />
          <span className="sr-only">Close</span>
        </SheetPrimitive.Close>
      </div>
    </>
  </div>
));
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
);
SheetFooter.displayName = "SheetFooter";

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("text-foreground text-lg font-semibold", className)}
    {...props}
  />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("text-muted-foreground text-sm", className)}
    {...props}
  />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

const Sheet = {
  Root: SheetRoot,
  Trigger: SheetTrigger,
  Close: SheetClose,
  Content: SheetContent,
  SubContent: SheetSubContent,
  Header: SheetHeader,
  Footer: SheetFooter,
  Title: SheetTitle,
  Description: SheetDescription,
  useSheet
};

export { Sheet };
